/**
 * Password Strength Checker Tool
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';

type ZxcvbnResult = {
  score: number;
  guesses: number;
  feedback?: {
    warning?: string;
    suggestions?: string[];
  };
};

function unwrapDefault<T>(mod: unknown): T {
  const m = mod as Record<string, unknown> | null | undefined;
  return (m?.default ?? mod) as T;
}

export const PasswordStrengthCheckerTool: ITransformerTool = {
  id: 'password-strength-checker',
  name: 'Password Strength Checker',
  description: 'Estimate password strength and provide suggestions (zxcvbn)',
  version: '1.0.0',
  type: 'transformer',
  category: 'Validators & Testers',
  keywords: ['password', 'strength', 'security', 'check'],

  supportedInputTypes: [SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [],

  async transform(input: string): Promise<string> {
    const pw = String(input ?? '').replace(/\r\n/g, '\n').trim();
    if (!pw) return 'Score: 0/4\nStrength: Empty\nSuggestion: Enter a password to evaluate.';

    try {
      const mod = await import('@zxcvbn-ts/core');
      const zxcvbn = unwrapDefault<(password: string) => ZxcvbnResult>((mod as Record<string, unknown>).zxcvbn ?? mod);
      const result = zxcvbn(pw);
      const score = Math.max(0, Math.min(4, Number(result.score ?? 0)));

      const strengthLabel = ['Very weak', 'Weak', 'Fair', 'Strong', 'Very strong'][score] ?? 'Unknown';

      const lines: string[] = [];
      lines.push(`Score: ${score}/4`);
      lines.push(`Strength: ${strengthLabel}`);
      lines.push(`Length: ${pw.length}`);

      if (Number.isFinite(result.guesses)) {
        lines.push(`Guesses (est.): ${result.guesses}`);
      }

      const warning = result.feedback?.warning?.trim();
      if (warning) {
        lines.push('Warning:');
        lines.push(`- ${warning}`);
      }

      const suggestions = (result.feedback?.suggestions ?? []).map((s) => String(s).trim()).filter(Boolean);
      const uniqSuggestions = Array.from(new Set(suggestions));
      if (uniqSuggestions.length > 0) {
        lines.push('Suggestions:');
        for (const s of uniqSuggestions) lines.push(`- ${s}`);
      }

      return lines.join('\n');
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to evaluate password strength';
      throw ToolError.execution(msg);
    }
  },
};
